'use client'

import { useEffect, useState, useMemo } from 'react'
import Image from 'next/image'
import { SiteNav } from '@/components/ui/site-nav'
import { SiteFooter } from '@/components/ui/site-footer'

type LeaderboardPlayer = {
  address: string
  displayName: string
  wins: number
  losses: number
  draws: number
  proofs: number
  survivals?: number
  points?: number
  gamesPlayed: number
  winRate: number
  lastPlayedAt: string | null
  /** Consecutive wins ending at the player's most recent game (0 = streak broken). */
  currentStreak?: number
  /** Longest run of consecutive wins in the window. */
  bestStreak?: number
}

type SeasonBoard = {
  id: string
  name: string
  startsAt: string | null
  endsAt: string | null
  current: boolean
  games: number
  rows: LeaderboardPlayer[]
}

type MonthBoard = {
  id: string
  name: string
  startsAt: string
  endsAt: string
  current: boolean
  games: number
  rows: LeaderboardPlayer[]
}

type LeaderboardResponse = {
  global?: LeaderboardPlayer[]
  monthly?: LeaderboardPlayer[]
  months?: MonthBoard[]
  seasons?: SeasonBoard[]
  /** Legacy field — older backend deploys return only this (all-time rows). */
  players?: LeaderboardPlayer[]
  totalGames: number
  monthlyGames?: number
  generatedAt: string
}

// 'monthly', or 'season:<id>', or the legacy 'global' fallback.
type Tab = string

/** Admin-editable bounty card content (backend /api/config/bounty). */
type BountyConfig = {
  active: boolean
  title: string
  body: string
  prize?: string
  endsAt?: string
}

// Mirrors POINTS in backend/src/routes/leaderboard.ts — keep in sync.
const POINTS = { win: 7, draw: 5, loss: 2, shield: 3 } as const

/** Fallback for rows from a backend that predates server-side points. */
function computePoints(p: LeaderboardPlayer): number {
  return (
    p.wins * POINTS.win +
    p.draws * POINTS.draw +
    p.losses * POINTS.loss +
    p.proofs * POINTS.shield
  )
}

function pointsOf(p: LeaderboardPlayer): number {
  return p.points ?? computePoints(p)
}

function sortByPoints(players: LeaderboardPlayer[]): LeaderboardPlayer[] {
  return [...players].sort((a, b) =>
    pointsOf(b) - pointsOf(a) || b.wins - a.wins || b.proofs - a.proofs
  )
}

/** "active 2d ago"-style label from the player's newest finished game. */
function activeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 60) return 'active just now'
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `active ${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days <= 30) return `active ${days}d ago`
  return `last seen ${new Date(iso).toLocaleDateString()}`
}

function seasonWindow(s: SeasonBoard): string {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en', { month: 'short', year: 'numeric' })
  const start = s.startsAt ? fmt(s.startsAt) : 'Genesis'
  const end = s.endsAt ? fmt(s.endsAt) : 'ongoing'
  return `${start} → ${end}`
}

const RANK_COLORS = ['#f5c518', '#8fa882', '#cd7f32']

// Shared grid template — header and rows must match exactly. Phones get a
// 3-column layout (#, operative, points; wins/shields live in the W/L/D chip)
// so the table never forces horizontal scrolling on narrow screens.
const ROW_GRID_CLASS =
  'grid-cols-[2rem_minmax(0,1fr)_3.5rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_5rem_4rem_4.5rem] gap-2 sm:gap-4 px-3 sm:px-5'

function PlayerRow({ player, rank }: { player: LeaderboardPlayer; rank: number }) {
  const rankColor = RANK_COLORS[rank - 1] ?? '#7d9a72'
  const isTop3 = rank <= 3
  const streak = player.currentStreak ?? 0
  const best = player.bestStreak ?? 0

  return (
    <div
      className={`rise-in grid items-center ${ROW_GRID_CLASS} rounded-xl border py-3 transition-all duration-150 hover:scale-[1.005] sm:py-4`}
      style={{
        borderColor: isTop3 ? `${rankColor}44` : 'rgba(107,142,35,0.12)',
        backgroundColor: isTop3 ? `${rankColor}0a` : '#0e180d',
        animationDelay: `${Math.min((rank - 1) * 45, 450)}ms`,
      }}
    >
      {/* Rank — #1 gets the slow toxic glow */}
      <div
        className={`flex h-7 w-7 items-center justify-center rounded-lg font-heading text-base leading-none sm:h-9 sm:w-9 sm:text-lg${rank === 1 ? ' toxic-pulse' : ''}`}
        style={{ backgroundColor: `${rankColor}22`, color: rankColor }}
      >
        {rank}
      </div>

      {/* Player info */}
      <div className="min-w-0">
        {/* Names stay small on phones — long operative names at text-lg crowded
            the row and pushed the meta chips onto three lines. */}
        <p className="truncate font-heading text-sm leading-tight sm:text-lg sm:leading-none" style={{ color: '#d4c9b2' }}>
          {player.displayName}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {streak >= 2 && (
            <span
              className="rounded-full px-2 py-0.5 font-mono text-[10px] font-bold uppercase"
              style={{ backgroundColor: 'rgba(245,197,24,0.14)', color: '#f5c518' }}
              title={`${streak} wins in a row${best > streak ? ` · best ${best}` : ''}`}
            >
              🔥 {streak} streak
            </span>
          )}
          {player.lastPlayedAt && (
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.12em] sm:inline" style={{ color: '#7d9a72' }}>
              {activeAgo(player.lastPlayedAt)}
            </span>
          )}
          <span
            className="rounded-full px-2 py-0.5 font-mono text-[10px] uppercase"
            style={{ backgroundColor: 'rgba(245,197,24,0.1)', color: '#f5c518' }}
          >
            {Math.round(player.winRate * 100)}% win rate
          </span>
          <span
            className="rounded-full px-2 py-0.5 font-mono text-[10px] uppercase"
            style={{
              backgroundColor: player.wins >= player.losses ? 'rgba(26,122,74,0.15)' : 'rgba(230,51,41,0.15)',
              color: player.wins >= player.losses ? '#1a7a4a' : '#e63329',
            }}
          >
            {player.wins}W / {player.losses}L / {player.draws}D
          </span>
        </div>
      </div>

      {/* Points */}
      <div className="text-center">
        <p className="font-heading text-xl sm:text-2xl leading-none" style={{ color: '#f5c518' }}>{pointsOf(player).toLocaleString()}</p>
      </div>

      {/* Wins — desktop only; phones see it in the W/L/D chip */}
      <div className="hidden text-center sm:block">
        <p className="font-heading text-2xl leading-none" style={{ color: '#6b8e23' }}>{player.wins}</p>
      </div>

      {/* Shields — desktop only */}
      <div className="hidden text-center sm:block">
        <p className="font-heading text-2xl leading-none" style={{ color: '#e63329' }}>{player.proofs}</p>
      </div>
    </div>
  )
}

/** The month's #1 styled as a collectible. A component because it renders in
    two spots: above the table on phones, in the sidebar on desktop. */
function ChampionCard({ champion, monthName, boardSize, loading }: {
  champion: LeaderboardPlayer | null
  monthName: string
  boardSize: number
  loading: boolean
}) {
  return (
    <div className="champion-scene rise-in">
      <div className="champion-card">
        <div className="champion-inner p-5 text-center">
          <div className="champion-backdrop" aria-hidden />
          <div className="relative">
            <p
              className="font-heading text-xl font-bold uppercase tracking-[0.14em]"
              style={{ color: '#f5c518', textShadow: '0 0 12px rgba(245,197,24,0.35)' }}
            >
              ★ {monthName} Champion ★
            </p>
            {champion ? (
              <>
                <div className="champion-avatar mx-auto mt-4">
                  <Image
                    src="/images/bg-patient-zero.webp"
                    alt=""
                    width={112}
                    height={112}
                    className="champion-avatar-img"
                  />
                  <span className="champion-avatar-initial font-display">
                    {champion.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <p className="mt-3 truncate font-heading text-2xl leading-none" style={{ color: '#d4c9b2' }}>
                  {champion.displayName}
                </p>
                <div className="mt-4 flex justify-center gap-5">
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: '#7d9a72' }}>Points</p>
                    <p className="mt-1 font-heading text-2xl leading-none" style={{ color: '#f5c518' }}>{pointsOf(champion).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: '#7d9a72' }}>Wins</p>
                    <p className="mt-1 font-heading text-2xl leading-none" style={{ color: '#6b8e23' }}>{champion.wins}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: '#7d9a72' }}>Shields</p>
                    <p className="mt-1 font-heading text-2xl leading-none" style={{ color: '#e63329' }}>{champion.proofs}</p>
                  </div>
                </div>
              </>
            ) : (
              <p className="mt-4 font-mono text-xs" style={{ color: '#7d9a72' }}>
                {loading ? 'Loading…' : 'The throne is empty — top this month’s board to claim it.'}
              </p>
            )}
            <p
              className="mt-4 border-t pt-3 font-mono text-[9px] uppercase tracking-[0.22em]"
              style={{ color: '#7d9a72', borderColor: 'rgba(245,197,24,0.15)' }}
            >
              Zombie Plague · No. 1 of {boardSize || '—'}
            </p>
          </div>
          <div className="champion-sheen" />
        </div>
      </div>
    </div>
  )
}

const PAGE_SIZE = 10
// Phones get shorter pages — a 10-row list plus the sidebar cards is a
// tiring scroll on a small screen.
const MOBILE_PAGE_SIZE = 5

export default function LeaderboardPage() {
  const [data, setData] = useState<LeaderboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('monthly')
  const [page, setPage] = useState(1)
  const [bounty, setBounty] = useState<BountyConfig | null>(null)

  // Tracks Tailwind's sm breakpoint so phones get shorter pages.
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  const pageSize = isMobile ? MOBILE_PAGE_SIZE : PAGE_SIZE

  // Live bounty card content — independent of the leaderboard fetch so a
  // failure in either doesn't blank the other.
  useEffect(() => {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:4000'
    fetch(`${backendUrl}/api/config/bounty`)
      .then(r => (r.ok ? r.json() : null))
      .then(j => setBounty(j?.value ?? null))
      .catch(() => { /* keep the default teaser */ })
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const load = async () => {
      try {
        setLoading(true)
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:4000'
        const res = await fetch(`${backendUrl}/api/leaderboard`, { signal: controller.signal })
        if (!res.ok) throw new Error(`Leaderboard request failed: ${res.status}`)
        const json = await res.json() as LeaderboardResponse
        setData(json)
        setError(null)
      } catch (err) {
        if (controller.signal.aborted) return
        setError(err instanceof Error ? err.message : 'Failed to load leaderboard.')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void load()
    return () => controller.abort()
  }, [])

  const globalRows = useMemo(
    () => data ? sortByPoints(data.global ?? data.players ?? []) : [],
    [data]
  )
  // Legacy backends can't provide per-month aggregates; approximate with
  // all-time rows for players active this month until the backend redeploys.
  const monthlyRows = useMemo(() => {
    if (!data) return []
    if (data.monthly) return sortByPoints(data.monthly)
    const monthStart = new Date()
    monthStart.setUTCDate(1)
    monthStart.setUTCHours(0, 0, 0, 0)
    return sortByPoints(
      (data.players ?? []).filter(p => p.lastPlayedAt && new Date(p.lastPlayedAt) >= monthStart)
    )
  }, [data])

  const seasonBoards = useMemo(() => {
    const map = new Map<string, LeaderboardPlayer[]>()
    for (const s of data?.seasons ?? []) map.set(`season:${s.id}`, sortByPoints(s.rows))
    return map
  }, [data])

  // Current + past calendar months (months[0] is always the current one);
  // absent on a backend that predates month history.
  const monthBoards = useMemo(
    () => (data?.months ?? []).map(m => ({ ...m, rows: sortByPoints(m.rows) })),
    [data]
  )
  const [selectedMonthId, setSelectedMonthId] = useState<string | null>(null)
  const activeMonth = monthBoards.find(m => m.id === selectedMonthId) ?? monthBoards[0] ?? null

  // This Month first and default: a board that resets on the 1st is winnable
  // for a newcomer, while long-running totals read as an unreachable wall.
  // Seasons come newest-first; a backend without seasons degrades to Global.
  const tabs = useMemo<{ id: Tab; label: string }[]>(() => {
    const seasonTabs = (data?.seasons ?? [])
      .slice()
      .reverse()
      .map(s => ({ id: `season:${s.id}`, label: s.name }))
    return [
      { id: 'monthly', label: 'This Month' },
      ...(seasonTabs.length > 0 ? seasonTabs : [{ id: 'global', label: 'Global' }]),
    ]
  }, [data])

  const activeSeason = (data?.seasons ?? []).find(s => `season:${s.id}` === activeTab) ?? null

  const sorted =
    activeTab === 'monthly' ? (activeMonth?.rows ?? monthlyRows)
    : activeTab === 'global' ? globalRows
    : seasonBoards.get(activeTab) ?? globalRows

  // Reset paging (and month selection when leaving the tab) on any view change
  useEffect(() => { setPage(1) }, [activeTab, selectedMonthId, data, pageSize])
  useEffect(() => { setSelectedMonthId(null) }, [activeTab])

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageSlice = sorted.slice((page - 1) * pageSize, page * pageSize)
  const pageOffset = (page - 1) * pageSize

  const totalShields = useMemo(() => globalRows.reduce((s, p) => s + p.proofs, 0), [globalRows])
  const monthChampion = monthlyRows[0] ?? null
  const monthName = new Date().toLocaleString('en', { month: 'long' })

  return (
    <main
      className="min-h-screen"
      style={{
        backgroundColor: '#060b06',
        color: '#d4c9b2',
        backgroundImage: 'url(/images/bg-leaderboard.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(6,11,6,0.85)', zIndex: 0 }} />

      <div className="relative" style={{ zIndex: 1 }}>
        {/* Nav */}
        <div className="sticky top-0 z-50 px-4 pt-4 sm:px-8 sm:pt-6">
          <div className="mx-auto w-full max-w-6xl">
            <SiteNav currentPath="/leaderboard" />
          </div>
        </div>

        {/* Hero header */}
        <header className="px-6 py-10 text-center sm:py-14">
          <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-5">
            <h1
              className="font-display text-5xl font-black leading-none sm:text-7xl lg:text-8xl"
              style={{
                background: 'linear-gradient(135deg, #cc1414 0%, #c97a12 50%, #6b8e23 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              LEADERBOARD
            </h1>
            <p className="max-w-lg font-mono text-sm" style={{ color: '#8fa882' }}>
              Every game counts — wins, draws, shields and survival all earn points.
              The monthly board resets on the 1st; seasons carry the long record,
              and a closed season is archived forever.
            </p>

            {/* Tabs */}
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {tabs.map(tab => {
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="rounded-full border-2 px-5 py-2 font-mono text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:opacity-90"
                    style={{
                      borderColor: isActive ? '#6b8e23' : 'rgba(107,142,35,0.25)',
                      backgroundColor: isActive ? 'rgba(107,142,35,0.15)' : 'transparent',
                      color: isActive ? '#6b8e23' : '#7d9a72',
                    }}
                  >
                    {tab.label}
                  </button>
                )
              })}
            </div>

            {/* Month picker — current month plus archived past months */}
            {activeTab === 'monthly' && monthBoards.length > 1 && (
              <div className="flex flex-wrap justify-center gap-1.5">
                {monthBoards.map(m => {
                  const isActive = m.id === activeMonth?.id
                  return (
                    <button
                      key={m.id}
                      onClick={() => setSelectedMonthId(m.id)}
                      className="rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-all duration-150 hover:opacity-90"
                      style={{
                        borderColor: isActive ? 'rgba(245,197,24,0.5)' : 'rgba(107,142,35,0.2)',
                        backgroundColor: isActive ? 'rgba(245,197,24,0.1)' : 'transparent',
                        color: isActive ? '#f5c518' : '#7d9a72',
                      }}
                    >
                      {m.name}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Active board window */}
            <p className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: '#7d9a72' }}>
              {activeTab === 'monthly'
                ? activeMonth
                  ? `${activeMonth.name} — ${activeMonth.current ? 'resets on the 1st (UTC)' : 'archived month'}`
                  : `${monthName} — resets on the 1st (UTC)`
                : activeSeason
                  ? `${seasonWindow(activeSeason)}${activeSeason.current ? ' · current season' : ' · archived'}`
                  : 'All-time record'}
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="px-4 pb-20 sm:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-[1fr_300px]">

              {/* Champion NFT card — phones only, above the table; display:none
                  on lg so it doesn't occupy a desktop grid cell */}
              <div className="mx-auto w-full max-w-sm lg:hidden">
                <ChampionCard
                  champion={monthChampion}
                  monthName={monthName}
                  boardSize={monthlyRows.length}
                  loading={loading}
                />
              </div>

              {/* Table */}
              <article
                className="rise-in rounded-xl border p-5"
                style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.18)' }}
              >
                {/* Header row */}
                <div
                  className={`mb-3 grid items-center ${ROW_GRID_CLASS} rounded-lg border py-3`}
                  style={{
                    borderColor: 'rgba(107,142,35,0.25)',
                    backgroundColor: '#0e180d',
                  }}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>#</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>Operative</span>
                  <span className="text-center font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#f5c518' }}>Points</span>
                  <span className="hidden text-center font-mono text-[10px] uppercase tracking-[0.2em] sm:block" style={{ color: '#6b8e23' }}>Wins</span>
                  <span className="hidden text-center font-mono text-[10px] uppercase tracking-[0.2em] sm:block" style={{ color: '#e63329' }}>Shields</span>
                </div>

                {/* Rows */}
                <div className="space-y-2">
                  {loading && (
                    <div className="rounded-xl border p-6 font-mono text-sm" style={{ borderColor: 'rgba(107,142,35,0.15)', color: '#7d9a72' }}>
                      Loading rankings…
                    </div>
                  )}
                  {error && (
                    <div className="rounded-xl border p-6 font-mono text-sm" style={{ borderColor: 'rgba(230,51,41,0.2)', color: '#e63329' }}>
                      {error}
                    </div>
                  )}
                  {!loading && !error && sorted.length === 0 && (
                    <div className="rounded-xl border p-6 font-mono text-sm" style={{ borderColor: 'rgba(107,142,35,0.15)', color: '#7d9a72' }}>
                      {activeTab === 'monthly'
                        ? `No games finished in ${monthName} yet — the first survivor tops this board.`
                        : activeSeason && !activeSeason.current
                          ? `No games were recorded in ${activeSeason.name}.`
                          : 'No completed games yet. Rankings will appear once rooms finish.'}
                    </div>
                  )}
                  {pageSlice.map((player, i) => (
                    <PlayerRow key={player.address} player={player} rank={pageOffset + i + 1} />
                  ))}
                </div>

                {/* Pagination */}
                {!loading && !error && totalPages > 1 && (
                  <div className="mt-5 flex items-center justify-between border-t pt-4" style={{ borderColor: 'rgba(107,142,35,0.12)' }}>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-lg border px-4 py-2 font-mono text-xs uppercase tracking-wider transition-all disabled:opacity-30"
                      style={{ borderColor: 'rgba(107,142,35,0.3)', color: '#6b8e23', backgroundColor: 'transparent' }}
                    >
                      ← Prev
                    </button>
                    <span className="font-mono text-xs" style={{ color: '#7d9a72' }}>
                      Page {page} / {totalPages}<span className="hidden sm:inline"> &nbsp;·&nbsp; {sorted.length} operatives</span>
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="rounded-lg border px-4 py-2 font-mono text-xs uppercase tracking-wider transition-all disabled:opacity-30"
                      style={{ borderColor: 'rgba(107,142,35,0.3)', color: '#6b8e23', backgroundColor: 'transparent' }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </article>

              {/* Sidebar */}
              <aside className="flex flex-col gap-5">

                {/* Champion NFT card — desktop only; phones get it above the table */}
                <div className="hidden lg:block">
                  <ChampionCard
                    champion={monthChampion}
                    monthName={monthName}
                    boardSize={monthlyRows.length}
                    loading={loading}
                  />
                </div>

                {/* Bounties teaser */}
                <div
                  className="rise-in rounded-xl border p-5"
                  style={{ backgroundColor: '#0a100a', borderColor: 'rgba(204,20,20,0.3)', animationDelay: '80ms' }}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#cc1414' }}>Bounties</p>
                  {bounty?.active ? (
                    <>
                      <p className="mt-3 font-heading text-xl leading-tight" style={{ color: '#d4c9b2' }}>
                        {bounty.title}
                      </p>
                      <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
                        {bounty.body}
                      </p>
                      {(bounty.prize || bounty.endsAt) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {bounty.prize && (
                            <span
                              className="rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase"
                              style={{ backgroundColor: 'rgba(245,197,24,0.12)', color: '#f5c518' }}
                            >
                              {bounty.prize}
                            </span>
                          )}
                          {bounty.endsAt && (
                            <span
                              className="rounded-full px-2.5 py-1 font-mono text-[10px] uppercase"
                              style={{ backgroundColor: 'rgba(107,142,35,0.12)', color: '#8fa882' }}
                            >
                              ends {bounty.endsAt}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="mt-3 font-heading text-xl leading-tight" style={{ color: '#d4c9b2' }}>
                        Early testers, this is your window.
                      </p>
                      <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
                        Bounty rewards are coming, funded by platform fees — and
                        they will be weighted toward the operatives who played
                        first. Every game, shield and win streak you record now is
                        permanent history that counts when the first bounty season
                        opens. Details will be announced here first.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span
                          className="rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase"
                          style={{ backgroundColor: 'rgba(245,197,24,0.12)', color: '#f5c518' }}
                        >
                          Early tester rewards
                        </span>
                        <span
                          className="rounded-full px-2.5 py-1 font-mono text-[10px] uppercase"
                          style={{ backgroundColor: 'rgba(107,142,35,0.12)', color: '#8fa882' }}
                        >
                          Season Zero · live now
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* How points work */}
                <div
                  className="rise-in rounded-xl border p-5"
                  style={{ backgroundColor: '#0a100a', borderColor: 'rgba(245,197,24,0.2)', animationDelay: '120ms' }}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#f5c518' }}>How Points Work</p>
                  <div className="mt-4 space-y-2">
                    {[
                      { label: 'Win a game',        value: `+${POINTS.win}`,    color: '#6b8e23' },
                      { label: 'Draw',              value: `+${POINTS.draw}`,   color: '#f5c518' },
                      { label: 'Shield used',       value: `+${POINTS.shield}`, color: '#e63329' },
                      { label: 'Loss (still counts)', value: `+${POINTS.loss}`, color: '#8fa882' },
                    ].map(s => (
                      <div
                        key={s.label}
                        className="flex items-center justify-between rounded-lg border px-4 py-2.5"
                        style={{ borderColor: 'rgba(107,142,35,0.15)', backgroundColor: '#0e180d' }}
                      >
                        <span className="font-mono text-xs" style={{ color: '#8fa882' }}>{s.label}</span>
                        <span className="font-heading text-xl leading-none" style={{ color: s.color }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 font-mono text-[10px] leading-relaxed" style={{ color: '#7d9a72' }}>
                    Shields are on-chain innocence proofs — each one costs the room&apos;s proof fee.
                  </p>
                  <p className="mt-2 font-mono text-[10px] leading-relaxed" style={{ color: '#7d9a72' }}>
                    🔥 Win streaks are tracked from your game history. They don&apos;t score
                    points yet — they&apos;re being recorded now so they can count toward
                    bounty payouts later.
                  </p>
                </div>

                {/* All-time stats */}
                <div
                  className="rise-in rounded-xl border p-5"
                  style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.18)', animationDelay: '240ms' }}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>All-Time Stats</p>
                  <div className="mt-4 space-y-2">
                    {[
                      { label: 'Total games',   value: loading ? '…' : String(data?.totalGames ?? 0), color: '#6b8e23' },
                      { label: 'Total shields', value: loading ? '…' : String(totalShields),          color: '#e63329' },
                      { label: 'Operatives',    value: loading ? '…' : String(globalRows.length),     color: '#f5c518' },
                    ].map(s => (
                      <div
                        key={s.label}
                        className="flex items-center justify-between rounded-lg border px-4 py-3"
                        style={{ borderColor: 'rgba(107,142,35,0.15)', backgroundColor: '#0e180d' }}
                      >
                        <span className="font-mono text-xs uppercase tracking-[0.14em]" style={{ color: '#7d9a72' }}>{s.label}</span>
                        <span className="font-heading text-2xl leading-none" style={{ color: s.color }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

              </aside>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <section className="px-6 py-20 text-center" style={{ backgroundColor: 'rgba(6,11,6,0.9)' }}>
          <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-6">
            <h2
              className="font-display text-4xl font-black leading-none sm:text-6xl"
              style={{
                background: 'linear-gradient(135deg, #cc1414, #c97a12)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              CLAIM YOUR RANK.
            </h2>
            <p className="font-mono text-sm sm:text-base" style={{ color: '#8fa882' }}>
              Join a match and prove your innocence — or conceal your guilt.
            </p>
            <a
              href="/lobby"
              className="rounded-lg border px-8 py-4 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg, #cc1414, #c97a12)',
                borderColor: 'transparent',
                color: '#060b06',
                boxShadow: '0 0 24px rgba(107,142,35,0.35)',
              }}
            >
              Enter the Lobby
            </a>
          </div>
        </section>

        <SiteFooter />
      </div>
    </main>
  )
}
